<!-- CreateModule.vue -->
<template>
  <BaseModal
    :is-visible="isVisible"
    title="Create New Module"
    subtitle="Add a new training module to the system"
    :icon="Plus"
    size="2xl"
    :show-footer="true"
    @close="closeModal"
  >
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Module Name -->
      <BaseInput
        v-model="formData.name"
        label="Module Name"
        type="text"
        placeholder="Enter module name"
        :required="true"
        :error="errors.name"
      />

      <!-- Module Description -->
      <BaseInput
        v-model="formData.description"
        label="Description"
        type="textarea"
        :rows="4"
        placeholder="Enter module description (optional)"
        :error="errors.description"
      />

      <!-- File Upload Section -->
      <div class="form-group">
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Module File
        </label>
        <div class="space-y-4">
          <!-- File Upload Area -->
          <div
            class="relative border-2 border-dashed rounded-xl p-6 transition-colors duration-200 cursor-pointer"
            :class="[
              isDragOver
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
            ]"
            @drop="onFileDrop"
            @dragover.prevent="onDragOver"
            @dragenter.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.doc,.docx"
              class="hidden"
              @change="onFileSelect"
            />
            
            <div v-if="!selectedFile" class="text-center">
              <template v-if="isDragOver">
                <Upload class="mx-auto h-12 w-12 text-blue-500 mb-4" />
                <div class="text-sm font-medium text-blue-600 dark:text-blue-400">
                  Release to drop your file
                </div>
                <p class="text-xs text-blue-500/80 dark:text-blue-400/80 mt-1">
                  PDF, DOC, DOCX up to 10MB
                </p>
              </template>
              <template v-else>
                <Upload class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
                <div class="text-sm text-gray-600 dark:text-gray-300">
                  <span class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer">
                    Click to upload
                  </span>
                  or drag and drop
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  PDF, DOC, DOCX up to 10MB
                </p>
              </template>
            </div>

            <!-- Selected File Display -->
            <div v-else class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div class="flex items-center">
                <FileText class="h-8 w-8 text-blue-500 mr-3" />
                <div>
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ selectedFile.name }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatFileSize(selectedFile.size) }}
                  </p>
                </div>
              </div>
              <button
                type="button"
                class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                @click.stop="removeFile"
                title="Remove file"
              >
                <X class="h-5 w-5" />
              </button>
            </div>
          </div>
          <p v-if="errors.file" class="text-sm text-red-600 font-medium">
            {{ errors.file }}
          </p>
        </div>
      </div>

      <!-- General error message -->
      <div v-if="errors.general" class="mt-3">
        <div class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium">
          <XCircle class="w-4 h-4 mr-2" />
          {{ errors.general }}
        </div>
      </div>

      <!-- Live validation messages -->
      <div class="mt-3 space-y-2">
        <div
          v-if="nameValidationMessage"
          class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
        >
          <XCircle class="w-4 h-4 mr-2" />
          {{ nameValidationMessage }}
        </div>
        <div
          v-if="isFormValid && !nameValidationMessage"
          class="flex items-center text-sm text-green-600 dark:text-green-400 font-medium"
        >
          <CheckCircle class="w-4 h-4 mr-2" />
          Ready to create module
        </div>
      </div>
    </form>

    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        :disabled="isLoading"
        @click="closeModal"
      />
      <BaseButton
        :text="isLoading ? 'Creating...' : 'Create Module'"
        :loading-text="'Creating...'"
        variant="primary"
        :disabled="isLoading || !isFormValid"
        :loading="isLoading"
        @click="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  Plus,
  Upload,
  FileText,
  X,
  XCircle,
  CheckCircle
} from 'lucide-vue-next'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import BaseInput from '@/components/common/BaseInput.vue'
import { useToastService } from '@/services/toast'
import ModulesService from '@/services/modules'

interface Props {
  isVisible: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}

interface ModuleData {
  name: string
  description: string
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const toast = useToastService()

// Form state
const formData = ref<ModuleData>({
  name: '',
  description: ''
})

const selectedFile = ref<File | null>(null)
const errors = ref<Record<string, string>>({})
const isLoading = ref(false)
const isDragOver = ref(false)
const dragCounter = ref(0)
const fileInput = ref<HTMLInputElement | null>(null)

// Computed properties
const isFormValid = computed(() => {
  return formData.value.name.trim().length >= 3
})

const nameValidationMessage = computed(() => {
  if (!formData.value.name || formData.value.name.trim() === '') {
    return 'Module name is required'
  }
  if (formData.value.name.trim().length < 3) {
    return 'Module name must be at least 3 characters'
  }
  return ''
})

// File handling methods
const onFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    handleFileSelection(target.files[0])
  }
}

const onFileDrop = (event: DragEvent) => {
  event.preventDefault()
  const file = event.dataTransfer?.files[0]
  if (file) {
    handleFileSelection(file)
  }
  // Reset drag state on drop
  dragCounter.value = 0
  isDragOver.value = false
}

const onDragOver = (event: DragEvent) => {
  event.preventDefault()
  if (!isDragOver.value) isDragOver.value = true
}

const onDragEnter = (event: DragEvent) => {
  event.preventDefault()
  dragCounter.value += 1
  isDragOver.value = true
}

const onDragLeave = (event: DragEvent) => {
  event.preventDefault()
  dragCounter.value = Math.max(0, dragCounter.value - 1)
  if (dragCounter.value === 0) {
    isDragOver.value = false
  }
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileSelection = (file: File) => {
  errors.value.file = ''
  
  // Validate file type
  const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  if (!allowedTypes.includes(file.type)) {
    errors.value.file = 'Please select a PDF, DOC, or DOCX file'
    return
  }
  
  // Validate file size (10MB limit)
  if (file.size > 10 * 1024 * 1024) {
    errors.value.file = 'File size must be less than 10MB'
    return
  }
  
  selectedFile.value = file
}

const removeFile = () => {
  selectedFile.value = null
  errors.value.file = ''
  dragCounter.value = 0
  isDragOver.value = false
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Form methods
const resetForm = () => {
  formData.value = {
    name: '',
    description: ''
  }
  selectedFile.value = null
  errors.value = {}
  isDragOver.value = false
  dragCounter.value = 0
}

const closeModal = () => {
  if (!isLoading.value) {
    resetForm()
    emit('close')
  }
}

const validateForm = (): boolean => {
  errors.value = {}

  // Required fields validation
  if (!formData.value.name || formData.value.name.trim().length < 3) {
    errors.value.name = 'Module name must be at least 3 characters'
  }

  if (formData.value.description && formData.value.description.length > 1000) {
    errors.value.description = 'Description must be less than 1000 characters'
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isLoading.value = true
  errors.value = {}

  try {
    // Step 1: Create the module
    const modulePayload = {
      name: formData.value.name,
      description: formData.value.description || undefined
    }

    const moduleResponse = await ModulesService.createModule(modulePayload)
    console.log('Module creation response:', moduleResponse)

    if (moduleResponse.success) {
      const moduleId = moduleResponse.data.module_id
      console.log('Module ID:', moduleId)

      if (!moduleId) {
        console.error('Module ID is undefined in response:', moduleResponse.data)
        toast.error('Module created but ID is missing from response')
        emit('success')
        closeModal()
        return
      }

      // Step 2: Upload file if selected
      if (selectedFile.value) {
        try {
          console.log('Uploading file for module ID:', moduleId)
          const uploadResponse = await ModulesService.uploadModuleFile(moduleId, selectedFile.value)
          console.log('File upload response:', uploadResponse)
        } catch (fileError) {
          console.error('File upload failed:', fileError)
          toast.warning('Module created, but file upload failed')
        }
      }

      toast.success('Module created successfully')
      emit('success')
      closeModal()
    } else {
      // Handle case where response has success: false (shouldn't happen with current backend logic)
      if ((moduleResponse as any).errors) {
        errors.value = {}
        Object.keys((moduleResponse as any).errors).forEach(field => {
          errors.value[field] = (moduleResponse as any).errors[field][0]
        })
      } else {
        errors.value.general = moduleResponse.message || 'Failed to create module'
      }
    }
  } catch (err: any) {
    console.error('Module creation failed:', err)

    // Handle validation errors - only show inline field errors, no toast
    if (err?.errors) {
      // Clear any previous errors first
      errors.value = {}
      // Set specific field errors
      Object.keys(err.errors).forEach(field => {
        errors.value[field] = err.errors[field][0]
      })
    } else if (err?.response?.data?.errors) {
      // Fallback for cases where the error structure is different
      errors.value = {}
      Object.keys(err.response.data.errors).forEach(field => {
        errors.value[field] = err.response.data.errors[field][0]
      })
    } else {
      // For non-validation errors, we can still show a toast or handle differently
      // But for now, let's also make this inline
      const message = err?.message || err?.response?.data?.message || 'Failed to create module'
      // Set a general error instead of toast
      errors.value.general = message
    }
  } finally {
    isLoading.value = false
  }
}

// Watch for modal visibility changes to reset form
watch(
  () => props.isVisible,
  newValue => {
    if (!newValue) {
      resetForm()
    }
  }
)
</script>


